import { useState } from 'react';
import { Button, Card, Chip, Stack, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { useTranslate } from 'react-admin';
import { useCanWrite } from '@/access/useCanWrite';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { EmptyState } from '@/components/shell/EmptyState';
import { ErrorState } from '@/components/shell/ErrorState';
import { LoadingState } from '@/components/shell/LoadingState';
import { payApiClient } from '@/api/client';
import { formatMinorUnits } from '@/utils/amount';
import { useApiResource } from '@/hooks/useApiResource';
import type { AdminTenantPolicy } from '@/api/models';
import { TenantPolicyForm } from './TenantPolicyForm';

interface TenantPolicyPanelProps {
  tenantCode: string;
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right' }}>
        {value}
      </Typography>
    </Stack>
  );
}

function PolicyView({ policy, translate }: { policy: AdminTenantPolicy; translate: (key: string) => string }) {
  return (
    <Stack spacing={1.5}>
      <Field
        label={translate('pay.tenants.policy.fieldMaxAmount')}
        value={formatMinorUnits(policy.maxAmountMinor, policy.currencies[0] ?? 'EUR')}
      />
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {translate('pay.tenants.policy.fieldCurrencies')}
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end">
          {policy.currencies.map((currency) => (
            <Chip key={currency} label={currency} size="small" />
          ))}
        </Stack>
      </Stack>
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {translate('pay.tenants.policy.fieldAllowedTypes')}
        </Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end">
          {policy.allowedTypes.map((type) => (
            <Chip key={type} label={translate(`pay.operations.type.${type}`)} size="small" />
          ))}
        </Stack>
      </Stack>
      <Stack spacing={0.5}>
        <Typography variant="body2" color="text.secondary">
          {translate('pay.tenants.policy.fieldAllowedReturnOrigins')}
        </Typography>
        {policy.allowedReturnOrigins.map((origin) => (
          <Typography key={origin} variant="body2" sx={{ fontFamily: (theme) => theme.custom.fontFamilyMono, fontSize: '0.8rem' }}>
            {origin}
          </Typography>
        ))}
      </Stack>
      <Field
        label={translate('pay.tenants.policy.fieldConflictPolicy')}
        value={translate(`pay.tenants.policy.conflictPolicy.${policy.orderReferenceConflictPolicy}`)}
      />
    </Stack>
  );
}

/**
 * Tenant policy panel against `GET`/`PUT /v1/admin/tenants/{code}/policy`.
 * A `404` (no policy configured yet) renders a designed empty state with a
 * "Crear política" action instead of a blank panel — `PUT` creates or
 * replaces the whole row either way, per the contract.
 */
export function TenantPolicyPanel({ tenantCode }: TenantPolicyPanelProps) {
  const translate = useTranslate();
  const canWrite = useCanWrite();
  const [editing, setEditing] = useState(false);
  const { data, loading, error, reload } = useApiResource(() => payApiClient.getAdminTenantPolicy(tenantCode), tenantCode);

  const cardSx: SxProps<Theme> = { p: 2.5 };

  if (loading) {
    return (
      <Card>
        <LoadingState variant="card" />
      </Card>
    );
  }

  if (error && error.errorCode !== 'not_found') {
    if (error.errorCode === 'access_denied') {
      return (
        <Card>
          <AccessDenied />
        </Card>
      );
    }
    return (
      <Card>
        <ErrorState title={translate('pay.tenants.policy.errorTitle')} body={error.message} onRetry={reload} />
      </Card>
    );
  }

  function handleSaved() {
    setEditing(false);
    reload();
  }

  if (!data) {
    if (editing) {
      return (
        <Card sx={cardSx}>
          <TenantPolicyForm tenantCode={tenantCode} onSaved={handleSaved} onCancel={() => setEditing(false)} />
        </Card>
      );
    }
    return (
      <Card>
        <EmptyState
          title={translate('pay.tenants.policy.emptyTitle')}
          body={translate('pay.tenants.policy.emptyBody')}
          action={
            canWrite ? (
              <Button variant="contained" size="small" onClick={() => setEditing(true)}>
                {translate('pay.tenants.policy.createAction')}
              </Button>
            ) : undefined
          }
        />
      </Card>
    );
  }

  return (
    <Card sx={cardSx}>
      {editing ? (
        <TenantPolicyForm tenantCode={tenantCode} initial={data} onSaved={handleSaved} onCancel={() => setEditing(false)} />
      ) : (
        <Stack spacing={2}>
          <PolicyView policy={data} translate={translate} />
          {canWrite && (
            <Stack direction="row" justifyContent="flex-end">
              <Button size="small" variant="outlined" onClick={() => setEditing(true)}>
                {translate('pay.tenants.policy.editAction')}
              </Button>
            </Stack>
          )}
        </Stack>
      )}
    </Card>
  );
}
